import {useFluxStore} from 'client/framework';
import classNames from 'classnames';
import React from 'react';
import sessionStore from 'client/Session/SessionStore';

import type {QuestionModelV1} from '@albert-io/models';

import {Icon, Text} from '@albert-io/atomic';

import {ANNOTATION_SHIELD_EXEMPT_PROPS} from 'client/components/PracticeView/Annotations/annotationFreeze';
import {SaveStatus} from 'client/components/QuestionTypes/QuestionType.types';

import QuestionTypeStore from 'client/components/QuestionTypes/QuestionType.store';

import './question-actions-wrapper.scss';
import {IssuesModalButton} from 'client/Modals/IssuesModal/IssuesModalButton';
import {isQuestionRestrictedForStudent} from 'client/components/ContentDiscovery/StudentRestrictions';

import NewTranslationToggle from 'client/NewTranslationToggle/NewTranslationToggle';
import {hasZustand} from 'client/components/QuestionTypes/V2/useQuestionStores';
import useQuestionAndDrafts from 'client/components/QuestionTypes/V2/useQuestionAndDrafts.hook';
import QuestionActionsMenu from '../QuestionActionsMenu';
interface Props extends PropsWithClassNameOptional, PropsWithChildrenRequired {
  activeQuestion?: QuestionModelV1;
  questionId: string;
  showTranslationToggle?: boolean;
  /** Overrides the translation toggle's `viewType`. */
  translationToggleViewType?: React.ComponentProps<typeof NewTranslationToggle>['viewType'];
  /**
   * Renders a single info ("ⓘ") menu (Question feedback + My usage + Copyright)
   * at the start of the left group, in place of the standalone feedback button.
   * Used by the fused FRQ grading footer, which hides the page-level
   * `<Footer minimal />` and relocates copyright here.
   */
  showOverflowMenu?: boolean;
  /** Forwarded to the info menu's "My usage" item (fused FRQ grading footer). */
  questionSetId?: string;
  userId?: string;
  /**
   * Whether the student has a persisted guess for this question — a question store cannot say.
   * Types without a V2 store get a placeholder that never learns about the guess, and the types
   * that do have one are handed the empty guess a graded assignment synthesizes for unanswered
   * questions, so neither group's `isSubmitted` distinguishes a real submission.
   */
  hasSubmittedAnswer?: boolean;
}

export default function QuestionActionsWrapper({
  questionId,
  activeQuestion,
  children,
  className,
  showTranslationToggle = false,
  translationToggleViewType = 'assignment-builder',
  showOverflowMenu = false,
  questionSetId,
  userId,
  hasSubmittedAnswer = false
}: Props) {
  const activeQuestionStore = activeQuestion?.getStore();

  // sessionStore is only the subscription anchor here; the selectors close over the
  // (possibly zustand or undefined) activeQuestionStore and guard accordingly.
  const legacySaveErrored = useFluxStore(
    sessionStore,
    () =>
      activeQuestionStore !== undefined &&
      !hasZustand(activeQuestionStore) &&
      activeQuestionStore.getSaveStatus() === SaveStatus.SAVE_ERROR
  );
  const legacyLoadErrored = useFluxStore(
    sessionStore,
    () =>
      activeQuestionStore !== undefined &&
      !hasZustand(activeQuestionStore) &&
      activeQuestionStore.getSaveStatus() === SaveStatus.LOAD_ERROR
  );
  const legacyHasDraftGuess = useFluxStore(
    sessionStore,
    () =>
      activeQuestionStore !== undefined &&
      !hasZustand(activeQuestionStore) &&
      activeQuestionStore.getHasDraftGuess() &&
      !activeQuestionStore.getDraftGuess().isDraftGuessEmpty()
  );

  // TODO: use custom hook to get this

  // `isSubmitted` is deliberately not read — see `hasSubmittedAnswer`.
  const {hasDraftGuess: zHasDraftGuess, getDraftStatusCode} = useQuestionAndDrafts(activeQuestion);

  const hasError =
    activeQuestionStore !== undefined &&
    (hasZustand(activeQuestionStore)
      ? getDraftStatusCode() === SaveStatus.SAVE_ERROR
      : legacySaveErrored);

  const hasLoadError =
    activeQuestionStore !== undefined &&
    (hasZustand(activeQuestionStore)
      ? getDraftStatusCode() === SaveStatus.LOAD_ERROR
      : legacyLoadErrored);

  const hasDraftGuess =
    activeQuestionStore !== undefined &&
    (hasZustand(activeQuestionStore) ? zHasDraftGuess : legacyHasDraftGuess);

  // A re-load can fail on a question whose draft is already here; the answer is still saved.
  const showsLoadError = hasLoadError && !hasDraftGuess;
  const showsError = hasError || showsLoadError;

  // Keyed variants make React swap the whole span instead of diffing its children: deleting bare
  // text nodes crashes when browser page translation has rewrapped them in <font> (HB 133528335).
  let bannerText = (
    <Text key='no-draft' color='secondary'>
      No answer saved.
    </Text>
  );

  if (hasError) {
    bannerText = (
      <Text key='save-error' color='negative'>
        Your answer was not saved. Please refresh your browser.
      </Text>
    );
  } else if (showsLoadError) {
    bannerText = (
      <Text key='load-error' color='negative'>
        Your saved answer could not be loaded. Please refresh your browser.
      </Text>
    );
  } else if (hasDraftGuess) {
    bannerText = (
      <Text key='draft-saved' color='tertiary'>
        <Text color='secondary'>Answer saved</Text> and will be submitted with your assignment.
      </Text>
    );
  }

  const showFeedbackButton = useFluxStore(sessionStore, (s) => s.hasValidSession());

  // Both resolve through sessionStore (shouldNotSaveDraft also reads QuestionTypeStore's
  // assignment id); same anchor-only pattern as the selectors above.
  const shouldNotSaveDraft = useFluxStore(sessionStore, () =>
    QuestionTypeStore.shouldNotSaveDraft()
  );
  const isRestrictedForStudent = useFluxStore(sessionStore, () =>
    isQuestionRestrictedForStudent(activeQuestion)
  );

  return (
    <div
      className={classNames(className, 'question-actions-wrapper')}
      {...ANNOTATION_SHIELD_EXEMPT_PROPS}
    >
      {/* Draft-save status is moot once the answer is in, including a stale draft-save failure:
          nothing resets `saveStatus` on submit, so keeping the error visible here would report a
          failure for an answer that did save. */}
      {!shouldNotSaveDraft && !isRestrictedForStudent && !hasSubmittedAnswer && (
        <div
          className={classNames('question-actions-wrapper__draft-guess-status', {
            'question-actions-wrapper__draft-guess-status--error': showsError,
            'question-actions-wrapper__draft-guess-status--has-draft-guess':
              !showsError && hasDraftGuess
          })}
        >
          {showsError && (
            <div className='question-actions-wrapper__draft-guess-status-icon-wrapper'>
              <Icon color='negative' icon={['far', 'cloud-slash']} />
            </div>
          )}
          {bannerText}
        </div>
      )}
      <div className='gap-x1 px-x3 py-x2 flex justify-end'>
        <div className='flex w-full items-center justify-between'>
          <div className='flex items-center'>
            {showOverflowMenu && (
              <QuestionActionsMenu
                questionId={questionId}
                showFeedback={showFeedbackButton}
                questionSetId={questionSetId}
                userId={userId}
              />
            )}
            {showTranslationToggle && (
              <NewTranslationToggle
                light
                popoverPlacement='top-start'
                variant='IconButton'
                viewType={translationToggleViewType}
              />
            )}
            {!showOverflowMenu && showFeedbackButton && (
              <IssuesModalButton questionId={questionId} />
            )}
          </div>
          <div className='gap-x1 flex'>{children}</div>
        </div>
      </div>
    </div>
  );
}
